import { signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase.config';

export interface AdminUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: 'admin' | 'super-admin';
  isActive: boolean;
  createdAt: Date;
  lastLogin: Date;
  permissions: string[];
}

class FirebaseAuthService {
  private static instance: FirebaseAuthService;
  private currentUser: User | null = null;
  private adminUser: AdminUser | null = null;
  private authStateListeners: Array<(user: User | null, adminUser: AdminUser | null) => void> = [];
  private isInitialized: boolean = false;

  static getInstance(): FirebaseAuthService {
    if (!FirebaseAuthService.instance) {
      FirebaseAuthService.instance = new FirebaseAuthService();
    }
    return FirebaseAuthService.instance;
  }

  constructor() {
    console.log('🔥 FirebaseAuthService: Initializing Firebase authentication');
    this.initializeAuth();
  }

  private initializeAuth() {
    // Single auth state listener for the entire app
    onAuthStateChanged(auth, async (user) => {
      console.log('🔥 FirebaseAuthService: Auth state changed', user);
      this.currentUser = user;

      if (user) {
        try {
          this.adminUser = await this.loadAdminUser(user);
          console.log('🔥 FirebaseAuthService: Admin user loaded', this.adminUser);
        } catch (error) {
          console.error('🔥 FirebaseAuthService: Error loading admin user', error);
          this.adminUser = null;
        }
      } else {
        this.adminUser = null;
      }

      this.isInitialized = true;
      // Notify all listeners
      this.notifyListeners();
    });
  }

  private notifyListeners() {
    this.authStateListeners.forEach(listener => {
      listener(this.currentUser, this.adminUser);
    });
  }

  private async loadAdminUser(user: User): Promise<AdminUser | null> {
    try {
      console.log('🔍 Looking for admin user with UID:', user.uid);
      
      // Try exact UID first
      let userDoc = await getDoc(doc(db, 'admins', user.uid));
      
      if (!userDoc.exists()) {
        console.log('⚠️ Document not found with exact UID, trying alternative lookup...');
        
        // Try to find by email in case of UID mismatch
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(collection(db, 'admins'), where('email', '==', user.email));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          console.log('✅ Found admin user by email lookup');
          const docData = querySnapshot.docs[0].data();
          return {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            role: docData.role || 'admin',
            isActive: docData.isActive !== false,
            createdAt: docData.createdAt?.toDate() || new Date(),
            lastLogin: docData.lastLogin?.toDate() || new Date(),
            permissions: docData.permissions || []
          };
        }
        
        console.log('❌ No admin user found for email:', user.email);
        return null;
      }
      
      console.log('✅ Found admin user with exact UID');
      const data = userDoc.data();
      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role: data.role || 'admin',
        isActive: data.isActive !== false,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate() || new Date(),
        permissions: data.permissions || []
      };
    } catch (error) {
      console.error('Error loading admin user:', error);
      return null;
    }
  }

  async signIn(email: string, password: string): Promise<{ success: boolean; message: string; requiresMFA?: boolean }> {
    try {
      console.log('🔥 FirebaseAuthService: Attempting sign in for', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user is admin
      const adminUser = await this.loadAdminUser(user);
      if (!adminUser) {
        await signOut(auth);
        return {
          success: false,
          message: 'User not found in admin database'
        };
      }

      if (!adminUser.isActive) {
        await signOut(auth);
        return {
          success: false,
          message: 'Account is deactivated'
        };
      }

      console.log('🔥 FirebaseAuthService: Sign in successful for admin', adminUser.role);
      return {
        success: true,
        message: 'Login successful'
      };
    } catch (error: any) {
      console.error('🔥 FirebaseAuthService: Sign in failed', error);
      return {
        success: false,
        message: error.message || 'Login failed'
      };
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      this.currentUser = null;
      this.adminUser = null;
      console.log('🔥 FirebaseAuthService: Sign out successful');
    } catch (error) {
      console.error('🔥 FirebaseAuthService: Sign out failed', error);
      throw error;
    }
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getAdminUser(): AdminUser | null {
    return this.adminUser;
  }

  isAuthenticated(): boolean {
    return !!this.currentUser && !!this.adminUser;
  }

  isAdmin(): boolean {
    return this.isAuthenticated() && this.adminUser?.role === 'admin' || this.adminUser?.role === 'super-admin';
  }

  onAuthStateChange(callback: (user: User | null, adminUser: AdminUser | null) => void) {
    console.log('🔥 FirebaseAuthService: Adding auth state listener');

    // Add to listeners array
    this.authStateListeners.push(callback);

    // If already initialized, call immediately with current state
    if (this.isInitialized) {
      callback(this.currentUser, this.adminUser);
    }

    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }
}

export default FirebaseAuthService;
